/**
 * Online / offline, from the OS where we can.
 *
 * `navigator.onLine` inside a WebView lags (and on iOS often stays true with no route), so native reads
 * `@capacitor/network` (getStatus + networkStatusChange); the web, and binaries without the plugin, fall back to
 * `navigator.onLine` with the window `online` / `offline` events.
 *
 *   const { online } = useNetworkStatus()      // shared readonly ref
 *   const off = onNetworkChange((up) => …)     // fires on changes only, not the initial value
 *
 * The agent stream pauses its reconnect backoff while offline and reconnects the moment the network returns
 * (wired in src/native/platform.ts).
 */
import { Network } from '@capacitor/network'
import { Capacitor } from '@capacitor/core'
import { readonly, ref } from 'vue'
import { isNativePlatform } from '@/utils/platform'

const online = ref(typeof navigator === 'undefined' ? true : navigator.onLine !== false)
const listeners = new Set<(online: boolean) => void>()
let started = false

/** Apply a new status. Exposed for the dev lab and tests; the plugin and window events call it on device. */
export function setNetworkOnline(next: boolean): void {
    if (online.value === next) return
    online.value = next
    for (const fn of [...listeners]) {
        try {
            fn(next)
        } catch (err) {
            console.error('[network] listener failed', err)
        }
    }
}

export function onNetworkChange(fn: (online: boolean) => void): () => void {
    listeners.add(fn)
    return () => {
        listeners.delete(fn)
    }
}

export function isOnline(): boolean {
    return online.value
}

/** Start watching (once). Safe to call from anywhere; the first caller wins. */
export function startNetworkWatch(): void {
    if (started || typeof window === 'undefined') return
    started = true
    if (isNativePlatform() && Capacitor.isPluginAvailable('Network')) {
        void Network.getStatus()
            .then((s) => setNetworkOnline(s.connected))
            .catch(() => {})
        void Network.addListener('networkStatusChange', (s) => setNetworkOnline(s.connected)).catch(() => {})
        return
    }
    window.addEventListener('online', () => setNetworkOnline(true))
    window.addEventListener('offline', () => setNetworkOnline(false))
}

export function useNetworkStatus() {
    startNetworkWatch()
    return { online: readonly(online) }
}

/** Test-only. */
export function __resetNetworkForTests(value = true): void {
    listeners.clear()
    online.value = value
}
